import { isValidIban, normaliseIban } from "@/lib/iban";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { AlertCircle, Landmark, Loader2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export type SepaMandateLabels = {
  title: string;
  creditorHeading: string;
  creditorNameLabel: string;
  creditorIdLabel: string;
  mandateReferenceLabel: string;
  mandateReferencePending: string;
  authorizationHeading: string;
  authorizationText: string;
  refundNoticeHeading: string;
  refundNoticeText: string;
  customerDataHeading: string;
  salutationLabel: string;
  salutationMr: string;
  salutationMrs: string;
  salutationDivers: string;
  firstName: string;
  lastName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  iban: string;
  bic: string;
  accountHolder: string;
  signatureHeading: string;
  signingPlace: string;
  signingDate: string;
  confirmCheckbox: string;
  typedNameLabel: string;
  typedNameHelper: string;
  submit: string;
  submitting: string;
  cancel: string;
  genericError: string;
  ibanInvalid: string;
  typedNameMismatch: string;
  mandatoryField: string;
};

type Salutation = "herr" | "frau" | "divers";

interface FormState {
  salutation: Salutation;
  firstName: string;
  lastName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  iban: string;
  bic: string;
  accountHolder: string;
  signingPlace: string;
  typedName: string;
  acceptsMandate: boolean;
}

const INITIAL_STATE: FormState = {
  salutation: "herr",
  firstName: "",
  lastName: "",
  street: "",
  houseNumber: "",
  postalCode: "",
  city: "",
  country: "Deutschland",
  iban: "",
  bic: "",
  accountHolder: "",
  signingPlace: "",
  typedName: "",
  acceptsMandate: false,
};

export interface SepaMandateFormProps {
  bookingId: number | null;
  creditorName: string;
  creditorId: string;
  labels: SepaMandateLabels;
  onSuccess: (mandateReference: string) => void;
  onCancel: () => void;
}

export function SepaMandateForm({
  bookingId,
  creditorName,
  creditorId,
  labels,
  onSuccess,
  onCancel,
}: SepaMandateFormProps) {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  /** After a failed submit, highlight every still-empty required field. */
  const isMissing = (key: keyof FormState) =>
    attempted && !String(form[key] ?? "").trim();
  const missingCls = (key: keyof FormState) =>
    isMissing(key)
      ? "border-destructive focus-visible:ring-destructive"
      : undefined;
  const missingLabelCls = (key: keyof FormState) =>
    isMissing(key) ? "text-destructive" : undefined;

  const signingDateDisplay = useMemo(
    () => format(new Date(), "dd.MM.yyyy | HH:mm"),
    []
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAttempted(true);

    const requiredKeys: Array<keyof FormState> = [
      "firstName",
      "lastName",
      "street",
      "houseNumber",
      "postalCode",
      "city",
      "iban",
      "accountHolder",
      "signingPlace",
      "typedName",
    ];
    for (const key of requiredKeys) {
      if (!String(form[key] ?? "").trim()) {
        setError(labels.mandatoryField);
        return;
      }
    }
    if (!isValidIban(form.iban)) {
      setError(labels.ibanInvalid);
      return;
    }
    if (!form.acceptsMandate) {
      setError(labels.confirmCheckbox);
      return;
    }
    const expected =
      `${form.firstName.trim()} ${form.lastName.trim()}`.toLowerCase();
    if (form.typedName.trim().toLowerCase() !== expected) {
      setError(labels.typedNameMismatch);
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "create-sepa-mandate",
        {
          body: {
            salutation: form.salutation,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            street: form.street.trim(),
            houseNumber: form.houseNumber.trim(),
            postalCode: form.postalCode.trim(),
            city: form.city.trim(),
            country: form.country.trim() || "Deutschland",
            iban: normaliseIban(form.iban),
            bic: form.bic.trim()
              ? form.bic.trim().toUpperCase().replace(/\s+/g, "")
              : undefined,
            accountHolder: form.accountHolder.trim(),
            signingPlace: form.signingPlace.trim(),
            typedName: form.typedName.trim(),
            acceptsMandate: form.acceptsMandate,
            bookingId,
          },
        }
      );
      if (invokeError) throw invokeError;
      const mandateReference = (data as any)?.mandateReference;
      if (!mandateReference) throw new Error(labels.genericError);
      toast.success(labels.title);
      onSuccess(mandateReference);
    } catch (err: any) {
      setError(err?.message || labels.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="p-4 sm:p-6 md:p-8 space-y-6">
        {/* Header — creditor identity + mandate reference */}
        <header className="space-y-3">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-xl font-bold text-foreground sm:text-2xl">
              {labels.title}
            </h2>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm grid gap-2 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                {labels.creditorNameLabel}
              </p>
              <p className="font-medium text-foreground">{creditorName}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                {labels.creditorIdLabel}
              </p>
              <p className="font-medium text-foreground font-mono">
                {creditorId}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs uppercase text-muted-foreground">
                {labels.mandateReferenceLabel}
              </p>
              <p className="font-medium text-foreground italic">
                {labels.mandateReferencePending}
              </p>
            </div>
          </div>
        </header>

        <Separator />

        {/* Section 1 — authorization */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            1. {labels.authorizationHeading}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {labels.authorizationText.replaceAll("{creditor}", creditorName)}
          </p>
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground leading-relaxed">
            <p className="font-medium text-foreground mb-1">
              {labels.refundNoticeHeading}
            </p>
            <p>{labels.refundNoticeText}</p>
          </div>
        </section>

        <Separator />

        {/* Section 2 — customer data */}
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            2. {labels.customerDataHeading}
          </h3>

          <div className="space-y-2">
            <Label>{labels.salutationLabel}</Label>
            <RadioGroup
              value={form.salutation}
              onValueChange={(v) => update("salutation", v as Salutation)}
              className="flex flex-wrap gap-4"
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="herr" />
                {labels.salutationMr}
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="frau" />
                {labels.salutationMrs}
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="divers" />
                {labels.salutationDivers}
              </label>
            </RadioGroup>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label
                htmlFor="sepa-firstName"
                className={missingLabelCls("firstName")}
              >
                {labels.firstName} *
              </Label>
              <Input
                id="sepa-firstName"
                className={missingCls("firstName")}
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="sepa-lastName"
                className={missingLabelCls("lastName")}
              >
                {labels.lastName} *
              </Label>
              <Input
                id="sepa-lastName"
                className={missingCls("lastName")}
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                autoComplete="family-name"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <div className="grid gap-3 grid-cols-[1fr_auto]">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="sepa-street"
                    className={missingLabelCls("street")}
                  >
                    {labels.street} *
                  </Label>
                  <Input
                    id="sepa-street"
                    className={missingCls("street")}
                    value={form.street}
                    onChange={(e) => update("street", e.target.value)}
                    autoComplete="address-line1"
                  />
                </div>
                <div className="space-y-1.5 w-24">
                  <Label
                    htmlFor="sepa-houseNumber"
                    className={missingLabelCls("houseNumber")}
                  >
                    {labels.houseNumber} *
                  </Label>
                  <Input
                    id="sepa-houseNumber"
                    className={missingCls("houseNumber")}
                    value={form.houseNumber}
                    onChange={(e) => update("houseNumber", e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="sepa-postalCode"
                className={missingLabelCls("postalCode")}
              >
                {labels.postalCode} *
              </Label>
              <Input
                id="sepa-postalCode"
                className={missingCls("postalCode")}
                value={form.postalCode}
                onChange={(e) => update("postalCode", e.target.value)}
                autoComplete="postal-code"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sepa-city" className={missingLabelCls("city")}>
                {labels.city} *
              </Label>
              <Input
                id="sepa-city"
                className={missingCls("city")}
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sepa-country">{labels.country}</Label>
              <Input
                id="sepa-country"
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                autoComplete="country-name"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label
                htmlFor="sepa-accountHolder"
                className={missingLabelCls("accountHolder")}
              >
                {labels.accountHolder} *
              </Label>
              <Input
                id="sepa-accountHolder"
                className={missingCls("accountHolder")}
                value={form.accountHolder}
                onChange={(e) => update("accountHolder", e.target.value)}
                autoComplete="cc-name"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sepa-iban" className={missingLabelCls("iban")}>
                {labels.iban} *
              </Label>
              <Input
                id="sepa-iban"
                className={missingCls("iban")}
                value={form.iban}
                onChange={(e) => update("iban", e.target.value.toUpperCase())}
                placeholder="DE89 3704 0044 0532 0130 00"
                autoComplete="off"
                inputMode="text"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sepa-bic">{labels.bic}</Label>
              <Input
                id="sepa-bic"
                value={form.bic}
                onChange={(e) => update("bic", e.target.value.toUpperCase())}
                placeholder="COBADEFFXXX"
                autoComplete="off"
              />
            </div>
          </div>
        </section>

        <Separator />

        {/* Section 3 — signature */}
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            3. {labels.signatureHeading}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label
                htmlFor="sepa-signingPlace"
                className={missingLabelCls("signingPlace")}
              >
                {labels.signingPlace} *
              </Label>
              <Input
                id="sepa-signingPlace"
                className={missingCls("signingPlace")}
                value={form.signingPlace}
                onChange={(e) => update("signingPlace", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sepa-signingDate">{labels.signingDate}</Label>
              <Input
                id="sepa-signingDate"
                value={`${signingDateDisplay} MEZ`}
                readOnly
                disabled
              />
            </div>
          </div>

          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer">
            <Checkbox
              checked={form.acceptsMandate}
              onCheckedChange={(v) => update("acceptsMandate", v === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-muted-foreground leading-snug">
              {labels.confirmCheckbox}
            </span>
          </label>

          <div className="space-y-1.5">
            <Label
              htmlFor="sepa-typedName"
              className={missingLabelCls("typedName")}
            >
              {labels.typedNameLabel} *
            </Label>
            <Input
              id="sepa-typedName"
              className={missingCls("typedName")}
              value={form.typedName}
              onChange={(e) => update("typedName", e.target.value)}
              placeholder={`${form.firstName} ${form.lastName}`.trim()}
            />
            <p className="text-xs text-muted-foreground">
              {labels.typedNameHelper}
            </p>
          </div>
        </section>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            {labels.cancel}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {labels.submitting}
              </>
            ) : (
              labels.submit
            )}
          </Button>
        </div>
      </Card>
    </form>
  );
}

export default SepaMandateForm;
